import {supabase} from '../lib/supabase';
import {User, Product} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';

class ApiService {
    private async getAuthHeaders(): Promise<HeadersInit> {
        try {

            // Add timeout to getSession call
            const sessionPromise = supabase.auth.getSession();
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Session timeout')), 5000)
            );


            const {data: {session}} = await Promise.race([
                sessionPromise,
                timeoutPromise
            ]) as any;

            const token = session?.access_token;

            const headers: Record<string, string> = {};
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
            return headers;
        } catch (error) {

            // Alternative approach - try to get session from localStorage

            try {
                const sessionString = localStorage.getItem('sb-mymwwqztvcobiyhdhsoy-auth-token');
                if (sessionString) {
                    const sessionData = JSON.parse(sessionString);
                    const token = sessionData?.access_token;

                    if (token) {
                        return {
                            Authorization: `Bearer ${token}`,
                        };
                    }
                }
            } catch (localError) {
            }

            return {};
        }
    }

    private async makeAdminRequest<T>(
        endpoint: string,
        adminToken: string,
        options: RequestInit = {}
    ): Promise<T> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        try {
            let finalHeaders: HeadersInit = {
                Authorization: `Bearer ${adminToken}`,
                ...(options.headers || {}),
            };
            const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
            if (!isFormData && typeof options.body === 'string') {
                if (finalHeaders instanceof Headers) {
                    if (!finalHeaders.has('Content-Type')) finalHeaders.set('Content-Type', 'application/json');
                } else {
                    const obj = finalHeaders as Record<string, string>;
                    if (!('Content-Type' in obj)) obj['Content-Type'] = 'application/json';
                    finalHeaders = obj;
                }
            }

            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                ...options,
                headers: finalHeaders,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const error = await response.json().catch(() => ({error: 'Network error'}));
                const message = error.error || `HTTP ${response.status}`;
                if (response.status === 403 && /blokiran/i.test(message)) {
                    sessionStorage.setItem('authErrorCode', 'BLOCKED_ACCOUNT');
                    await supabase.auth.signOut();
                    if (window.location.pathname !== '/login') {
                        window.location.replace('/login');
                    }
                }
                const err = new Error(message);
                (err as any).status = response.status;
                throw err;
            }

            return await response.json();
        } catch (error) {
            clearTimeout(timeoutId);
            if (error instanceof Error && error.name === 'AbortError') {
                throw new Error('Request timeout - backend may be down');
            }
            throw error;
        }
    }

    // Listings API
    async getPublicListings(params: {
        city?: string;
        minPrice?: number;
        maxPrice?: number;
        rooms?: number;
        sort?: string;
        limit?: number;
        offset?: number;
        purpose?: 'SALE' | 'RENT';
        q?: string;
    } = {}): Promise<{ items: any[]; count: number }> {
        const query = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null) query.append(k, String(v));
        });
        const qs = query.toString();
        return this.makeRequest<{ items: any[]; count: number }>(`/v1/listings${qs ? `?${qs}` : ''}`);
    }

    async getPublicListing(id: string): Promise<any> {
        return this.makeRequest<any>(`/v1/listings/${id}`);
    }

    async createListing(data: any): Promise<any> {
        return this.makeRequest<any>(`/v1/listings`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateListing(id: string, data: any): Promise<any> {
        return this.makeRequest<any>(`/v1/listings/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteListing(id: string): Promise<void> {
        await this.makeRequest(`/v1/listings/${id}`, {
            method: 'DELETE',
        });
    }

    async getContactInfo(listingId: string): Promise<any> {
        return this.makeRequest<any>(`/v1/listings/${listingId}/contact`);
    }

    async getMyListings(params: { limit?: number; offset?: number } = {}): Promise<{ items: any[]; count: number }> {
        const query = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null) query.append(k, String(v));
        });
        const qs = query.toString();
        return this.makeRequest<{ items: any[]; count: number }>(`/v1/listings/mine${qs ? `?${qs}` : ''}`);
    }

    async getMyListing(id: string): Promise<any> {
        return this.makeRequest<any>(`/v1/listings/mine/${id}`);
    }


    async uploadImage(listingId: string, file: File): Promise<any> {
        const form = new FormData();
        form.append('file', file);
        // Do not set Content-Type header manually; browser will set multipart boundary
        return this.makeRequest(`/v1/listings/${listingId}/images/upload`, {
            method: 'POST',
            body: form,
        } as any);
    }

    async deleteImage(listingId: string, imageId: string): Promise<void> {
        await this.makeRequest(`/v1/listings/${listingId}/images/${imageId}`, {method: 'DELETE'});
    }

    async reorderImages(listingId: string, positions: Record<string, number>): Promise<any> {
        return this.makeRequest(`/v1/listings/${listingId}/images/reorder`, {
            method: 'POST',
            body: JSON.stringify(positions),
        });
    }

    private async makeRequest<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const headers = await this.getAuthHeaders();

        // Add timeout to prevent infinite waiting
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        try {
            // Build headers: add Authorization, then merge options.headers. Set JSON Content-Type only for string bodies.
            let finalHeaders: HeadersInit = {...headers, ...(options.headers || {})};
            const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
            if (!isFormData && typeof options.body === 'string') {
                if (finalHeaders instanceof Headers) {
                    if (!finalHeaders.has('Content-Type')) finalHeaders.set('Content-Type', 'application/json');
                } else {
                    const obj = finalHeaders as Record<string, string>;
                    if (!('Content-Type' in obj)) obj['Content-Type'] = 'application/json';
                    finalHeaders = obj;
                }
            }

            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                ...options,
                headers: finalHeaders,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const error = await response.json().catch(() => ({error: 'Network error'}));
                const err = new Error(error.error || `HTTP ${response.status}`);
                (err as any).status = response.status;
                throw err;
            }

            const data = await response.json();
            return data;
        } catch (error) {
            clearTimeout(timeoutId);
            if (error instanceof Error && error.name === 'AbortError') {
                throw new Error('Request timeout - backend may be down');
            }
            throw error;
        }
    }

    async getUserProfile(): Promise<User> {
        return this.makeRequest<User>('/users/profile');
    }

    async updateUserProfile(data: { name?: string }): Promise<User> {
        return this.makeRequest<User>('/users/profile', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async purchaseTokens(amount: number): Promise<any> {
        return this.makeRequest<any>('/users/tokens/purchase', {
            method: 'POST',
            body: JSON.stringify({amount}),
        });
    }

    async getProducts(): Promise<{ items: Product[]; count: number }> {
        return this.makeRequest<{ items: Product[]; count: number }>(`/v1/products`);
    }

    async createPaymentOrder(productId: string): Promise<any> {
        return this.makeRequest<any>('/payments/orders', {
            method: 'POST',
            body: JSON.stringify({productId}),
        });
    }

    async buildPaymentPayload(orderId: string): Promise<any> {
        return this.makeRequest<any>(`/payments/${orderId}/build`, {
            method: 'POST',
        });
    }

    async useTokens(propertyId: string): Promise<any> {
        return this.makeRequest<any>('/users/tokens/use', {
            method: 'POST',
            body: JSON.stringify({property_id: propertyId}),
        });
    }

    async refreshUser(): Promise<User> {
        return this.makeRequest<User>('/users/refresh', {
            method: 'POST',
        });
    }

    async getFavorites(): Promise<any> {
        return this.makeRequest<any>('/users/favorites');
    }

    async addToFavorites(propertyId: string): Promise<any> {
        return this.makeRequest<any>(`/users/favorites/${propertyId}`, {
            method: 'POST',
        });
    }

    async removeFromFavorites(propertyId: string): Promise<any> {
        return this.makeRequest<any>(`/users/favorites/${propertyId}`, {
            method: 'DELETE',
        });
    }

    async getListings(): Promise<any> {
        return this.makeRequest<any>('/users/listings');
    }

    async addListing(propertyId: string): Promise<any> {
        return this.makeRequest<any>(`/users/listings/${propertyId}`, {
            method: 'POST',
        });
    }

    async removeListing(propertyId: string): Promise<any> {
        return this.makeRequest<any>(`/users/listings/${propertyId}`, {
            method: 'DELETE',
        });
    }

    async getPurchasedContacts(): Promise<any> {
        return this.makeRequest<any>('/users/purchased-contacts');
    }

    async checkPurchasedContact(propertyId: string): Promise<any> {
        return this.makeRequest<any>(`/users/purchased-contacts/${propertyId}`);
    }

    async adminLogin(username: string, password: string): Promise<{ token: string; expiresAt: number }> {
        return this.makeRequest<{ token: string; expiresAt: number }>('/auth/admin/login', {
            method: 'POST',
            body: JSON.stringify({username, password}),
        });
    }

    async adminGetUsers(adminToken: string): Promise<any[]> {
        return this.makeAdminRequest<any[]>('/admin/users', adminToken);
    }

    async adminGetUsersFiltered(adminToken: string, params: { q?: string; active?: boolean } = {}): Promise<any[]> {
        const query = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null) query.append(k, String(v));
        });
        const qs = query.toString();
        return this.makeAdminRequest<any[]>(`/admin/users${qs ? `?${qs}` : ''}`, adminToken);
    }

    async adminGetUserDetails(adminToken: string, userId: string): Promise<any> {
        return this.makeAdminRequest<any>(`/admin/users/${userId}`, adminToken);
    }

    async adminUpdateUserStatus(adminToken: string, userId: string, isActive: boolean, reason?: string): Promise<any> {
        return this.makeAdminRequest<any>(`/admin/users/${userId}/status`, adminToken, {
            method: 'POST',
            body: JSON.stringify({isActive, reason}),
        });
    }

    async adminUpdateUserNote(adminToken: string, userId: string, note?: string): Promise<any> {
        return this.makeAdminRequest<any>(`/admin/users/${userId}/note`, adminToken, {
            method: 'POST',
            body: JSON.stringify({note}),
        });
    }

    async adminAdjustUserTokens(adminToken: string, userId: string, delta: number, reason?: string): Promise<any> {
        return this.makeAdminRequest<any>(`/admin/users/${userId}/tokens`, adminToken, {
            method: 'POST',
            body: JSON.stringify({delta, reason}),
        });
    }

    async adminDeleteUser(adminToken: string, userId: string): Promise<any> {
        return this.makeAdminRequest<any>(`/admin/users/${userId}`, adminToken, {
            method: 'DELETE',
        });
    }

    async adminGetDashboard(adminToken: string): Promise<any> {
        return this.makeAdminRequest<any>('/admin/dashboard', adminToken);
    }

    async adminGetOrders(
        adminToken: string,
        params: { status?: string; paymentStatus?: string; includeTest?: boolean; userId?: string } = {}
    ): Promise<any[]> {
        const query = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null) query.append(k, String(v));
        });
        const qs = query.toString();
        return this.makeAdminRequest<any[]>(`/admin/orders${qs ? `?${qs}` : ''}`, adminToken);
    }

    async adminGetOrderDetails(adminToken: string, orderId: string): Promise<any> {
        return this.makeAdminRequest<any>(`/admin/orders/${orderId}`, adminToken);
    }

    async adminUpdateOrderStatus(adminToken: string, orderId: string, status: string): Promise<any> {
        return this.makeAdminRequest<any>(`/admin/orders/${orderId}/status`, adminToken, {
            method: 'POST',
            body: JSON.stringify({status}),
        });
    }

    async adminMarkOrderTest(adminToken: string, orderId: string): Promise<any> {
        return this.makeAdminRequest<any>(`/admin/orders/${orderId}/mark-as-test`, adminToken, {
            method: 'POST',
        });
    }

    async adminCheckOrderTransaction(adminToken: string, orderId: string): Promise<any> {
        return this.makeAdminRequest<any>(`/admin/orders/${orderId}/check-transaction`, adminToken, {
            method: 'POST',
        });
    }

    async adminGetListings(
        adminToken: string,
        params: { status?: string; ownerId?: string; purpose?: string; city?: string; q?: string; sort?: string; limit?: number; offset?: number } = {}
    ): Promise<{ items: any[]; count: number }> {
        const query = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null) query.append(k, String(v));
        });
        const qs = query.toString();
        return this.makeAdminRequest<{ items: any[]; count: number }>(`/admin/listings${qs ? `?${qs}` : ''}`, adminToken);
    }

    async adminGetListingDetails(adminToken: string, listingId: string): Promise<any> {
        return this.makeAdminRequest<any>(`/admin/listings/${listingId}`, adminToken);
    }

    async adminModerateListing(adminToken: string, listingId: string, status?: string, note?: string): Promise<any> {
        return this.makeAdminRequest<any>(`/admin/listings/${listingId}/moderate`, adminToken, {
            method: 'POST',
            body: JSON.stringify({status, note}),
        });
    }

    async adminGetProducts(adminToken: string): Promise<any[]> {
        return this.makeAdminRequest<any[]>('/admin/products', adminToken);
    }

    async adminCreateProduct(adminToken: string, payload: any): Promise<any> {
        return this.makeAdminRequest<any>('/admin/products', adminToken, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    async adminUpdateProduct(adminToken: string, productId: string, payload: any): Promise<any> {
        return this.makeAdminRequest<any>(`/admin/products/${productId}`, adminToken, {
            method: 'PUT',
            body: JSON.stringify(payload),
        });
    }
}

export const apiService = new ApiService(); 
